import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { StudentSection } from "./StudentLayout";
import { CreditCard, TriangleAlert, ArrowRight, Receipt } from "lucide-react";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  } catch { return iso; }
}

/**
 * Student billing page — one big status card + one primary CTA.
 * • Active: "Membership active · renews Aug 23" + Manage button
 * • Past due: red banner + "Update payment" button
 * • Inactive: "Start your membership" button that opens Tilopay checkout
 */
export default function StudentBilling() {
  const [data, setData] = useState(null);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [canceling, setCanceling] = useState(false);

  const refresh = () => {
    api.get("/billing/me").then(r => setData(r.data));
    api.get("/billing/history").then(r => setHistory(r.data));
  };
  useEffect(() => { refresh(); }, []);

  const cancel = async () => {
    const end = data.current_period_end ? new Date(data.current_period_end).toLocaleDateString() : null;
    const msg = end
      ? `Cancel your ${data.plan || "membership"}?\n\nYou'll keep training through ${end}. After that your subscription ends and card is no longer charged.`
      : `Cancel your ${data.plan || "membership"}?`;
    if (!window.confirm(msg)) return;
    setCanceling(true);
    try {
      await api.post("/billing/cancel", {});
      refresh();
    } catch (e) {
      alert(e.response?.data?.detail || "Could not cancel");
    } finally {
      setCanceling(false);
    }
  };

  const subscribe = async () => {
    setLoading(true);
    try {
      const r = await api.post("/billing/subscribe", { origin_url: window.location.origin });
      // Both mock (internal /payment/mock-checkout?…) and real Tilopay (external
      // https://…) responses are handled the same way: a top-level redirect.
      window.location.href = r.data.checkout_url;
    } catch (e) {
      alert(e.response?.data?.detail || "Could not start checkout. Try again in a minute.");
      setLoading(false);
    }
  };

  if (!data) return <div className="p-8 label">Loading…</div>;

  const status = data.status;
  const days = data.days_until_due;

  return (
    <div data-testid="page-student-billing">
      <StudentSection section="Membership" title="Billing" />
      <div className="px-5 space-y-4">

        {data.mock_mode && (
          <div className="card p-3 flex items-center gap-2" style={{ background: "rgba(244,185,66,0.06)", borderColor: "rgba(244,185,66,0.25)" }} data-testid="student-billing-sandbox">
            <TriangleAlert size={14} style={{ color: "rgb(244,185,66)" }} />
            <div className="label-tiny">Sandbox mode — no real card will be charged.</div>
          </div>
        )}

        <div className="card p-6 flex flex-col items-center text-center" data-testid="billing-hero">
          <div className="w-14 h-14 rounded-full flex items-center justify-center mb-3"
               style={{ background: "var(--bg-elev)", border: "1px solid var(--border)" }}>
            <CreditCard size={22} style={{ color: status === "active" ? "var(--green)" : "var(--text-dim)" }} />
          </div>

          {status === "active" && (
            <>
              <div className="label" style={{ color: "var(--green)" }}>Active</div>
              <div className="text-[26px] font-bold uppercase tracking-tight mt-1">${data.amount}<span className="text-[14px] mono font-normal" style={{ color: "var(--text-dim)" }}>/mo</span></div>
              {data.plan && <div className="label-tiny mt-1" data-testid="student-billing-plan">{data.plan}</div>}
              <div className="label-tiny mt-2">
                {days === 0 ? "Charges again today"
                  : days > 0 ? `Renews in ${days} day${days === 1 ? "" : "s"} · ${formatDate(data.current_period_end)}`
                  : `Overdue by ${Math.abs(days)} day${days === -1 ? "" : "s"}`}
              </div>
              <button onClick={subscribe} disabled={loading}
                      className="btn btn-outline w-full mt-5" data-testid="btn-manage-subscription">
                {loading ? "Loading…" : "Update payment method"}
              </button>
              <button onClick={cancel} disabled={canceling}
                      className="w-full mt-2 text-[12px] py-2"
                      style={{ color: "var(--text-dim)", background: "transparent", border: "none" }}
                      data-testid="btn-cancel-subscription">
                {canceling ? "Canceling…" : "Cancel membership"}
              </button>
            </>
          )}

          {status === "canceling" && (
            <>
              <div className="label" style={{ color: "rgb(244,185,66)" }}>Canceling · access ends soon</div>
              <div className="text-[22px] font-bold uppercase tracking-tight mt-1">${data.amount}<span className="text-[14px] mono font-normal" style={{ color: "var(--text-dim)" }}>/mo</span></div>
              {data.plan && <div className="label-tiny mt-1" data-testid="student-billing-plan">{data.plan}</div>}
              <div className="label-tiny mt-2">
                You&apos;ll keep training through {formatDate(data.current_period_end)}. Change your mind anytime before then.
              </div>
              <button onClick={subscribe} disabled={loading}
                      className="btn btn-primary w-full mt-5" data-testid="btn-resubscribe">
                {loading ? "Loading…" : "Reactivate membership"} <ArrowRight size={13}/>
              </button>
            </>
          )}

          {status === "past_due" && (
            <>
              <div className="label" style={{ color: "rgb(255,120,120)" }}>Payment failed</div>
              <div className="text-[22px] font-bold uppercase tracking-tight mt-1">Update to keep training</div>
              <div className="label-tiny mt-2">
                Your ${data.amount} monthly membership charge didn&apos;t go through. Add a new card to restore access.
              </div>
              <button onClick={subscribe} disabled={loading}
                      className="btn btn-primary w-full mt-5" data-testid="btn-fix-payment">
                {loading ? "Loading…" : "Update payment method"} <ArrowRight size={13}/>
              </button>
            </>
          )}

          {(status === "inactive" || status === "canceled" || !status) && (
            <>
              <div className="label" style={{ color: "var(--text-dim)" }}>Not subscribed</div>
              <div className="text-[26px] font-bold uppercase tracking-tight mt-1">${data.amount}<span className="text-[14px] mono font-normal" style={{ color: "var(--text-dim)" }}>/mo</span></div>
              <div className="label-tiny mt-2">
                Automatic monthly billing. Cancel anytime.
              </div>
              <button onClick={subscribe} disabled={loading}
                      className="btn btn-primary w-full mt-5" data-testid="btn-start-subscription">
                {loading ? "Loading…" : "Start membership"} <ArrowRight size={13}/>
              </button>
            </>
          )}
        </div>

        {history && history.rows.length > 0 && (
          <div className="card p-4" data-testid="student-billing-history">
            <div className="flex items-center justify-between mb-1">
              <div className="label">Payment history</div>
              <div className="label-tiny mono">Lifetime <span style={{ color: "var(--green)" }}>${history.total.toFixed(2)}</span></div>
            </div>
            <div className="divide-y" style={{ borderColor: "var(--border)" }}>
              {history.rows.map((r, i) => (
                <div key={r.session_id || i} className="py-3 flex items-center gap-3" data-testid={`billing-history-row-${i}`}>
                  <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
                       style={{ background: "var(--bg-elev)", border: "1px solid var(--border)" }}>
                    <Receipt size={14} style={{ color: "var(--green)" }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[13px]">${r.amount.toFixed(2)}</div>
                    <div className="label-tiny mt-0.5 capitalize">
                      {r.paid_at || (r.created_at || "").slice(0, 10)} · {r.method || "tilopay"} · {r.kind}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="label-tiny text-center mt-2" style={{ color: "var(--text-dim)" }}>
          Payments processed securely by Tilopay.
        </div>
      </div>
    </div>
  );
}
